"use client";

import { c as _c } from "react/compiler-runtime";
import { getSubtemplateConfig, type SubtemplateConfig } from "@repo/design-system/lib/client-registry";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { ClientsAndSubclients } from "~/setup/client";
import type { Job } from "~/types/job";
import type { SectionKey } from "../job-page/jobpagetemplate/unified-jobpage-template";
import { clientRegistry } from ".";
type ClientRegistry = typeof clientRegistry;
type ClientRegistryIndex = ClientRegistry[Extract<ClientsAndSubclients, keyof ClientRegistry>];
type EonUi = (typeof clientRegistry)["eon"]["ui"];
// Widen null-typed fields to string | null so subclient overrides (e.g. string values) are valid
type MergedUi = { [K in keyof EonUi]: null extends EonUi[K] ? string | null : EonUi[K] };
interface RegistryContextValue {
  client: ClientsAndSubclients;
  components: ClientRegistryIndex["components"];
  subtemplates: ClientRegistryIndex["subtemplates"];
  ui: MergedUi;
}
const RegistryContext = createContext<RegistryContextValue | undefined>(undefined);
export function RegistryProvider(t0) {
  const $ = _c(10);
  const {
    children,
    client
  } = t0;
  const registry = (client in clientRegistry ? clientRegistry[client as keyof typeof clientRegistry] : clientRegistry.eon) as ClientRegistryIndex;
  const eonUi = clientRegistry.eon.ui as Record<string, unknown>;
  const clientUi = registry.ui as Record<string, unknown>;
  let t1;
  if ($[0] !== clientUi) {
    t1 = {
      ...eonUi,
      ...Object.fromEntries(Object.entries(clientUi).filter(_temp))
    };
    $[0] = clientUi;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mergedUi = t1 as MergedUi;
  let t2;
  if ($[2] !== client || $[3] !== mergedUi || $[4] !== registry.components || $[5] !== registry.subtemplates) {
    t2 = {
      client,
      components: registry.components,
      ui: mergedUi,
      subtemplates: registry.subtemplates
    };
    $[2] = client;
    $[3] = mergedUi;
    $[4] = registry.components;
    $[5] = registry.subtemplates;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const value = t2;
  let t3;
  if ($[7] !== children || $[8] !== value) {
    t3 = <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
    $[7] = children;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp(t0) {
  const [, v] = t0;
  return v != null;
}
export function useRegistry() {
  const context = useContext(RegistryContext);
  if (!context) {
    throw new Error("useRegistry must be used within a RegistryProvider");
  }
  return context;
}
export function useJobPageTemplate(job) {
  const $ = _c(3);
  const {
    subtemplates
  } = useRegistry();
  if (!subtemplates || subtemplates.length === 0) {
    throw new Error("No subtemplates found in registry. This should never happen - check registry configuration.");
  }
  let t0;
  if ($[0] !== job || $[1] !== subtemplates) {
    t0 = getSubtemplateConfig(subtemplates, job);
    $[0] = job;
    $[1] = subtemplates;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const config = t0;
  if (!config) {
    const templateInfo = subtemplates.map(t => ({
      name: t.name,
      showProd: t.showProd,
      triggerResult: t.trigger(job)
    }));
    throw new Error(`No matching subtemplate config found for job. Subtemplates: ${JSON.stringify(templateInfo)}`);
  }
  return config as SubtemplateConfig<SectionKey>;
}